import {
    Todo,
    TodoWithDetails,
    TodoWithGoal,
    TodoWithUser,
    WorkspaceMemberWithUser,
} from '@/common/types/databaseTypes';
import { useComments } from '@/features/comment/services/useCommentQueries';
import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';
import { CreateTodoInput, todoService } from './todoService';
import {
    useTodoAssignedNotification,
    useTodoDeadlineChangedNotification,
    useTodoStateChangedNotification,
} from '@/features/notification/services/factoryNotificationServices';
import {
    NotificationType,
    UpdateContext,
} from '@/features/notification/constants/notification';
import { useAuthStore } from '@/stores/authStore';
import {
    checkSendNotificationByTodoStatus,
    isDifferent,
} from '@/features/notification/utils/notificationUtils';
import { getTodoStatus } from '@/common/utils/utils';
import { useDeleteSchedule } from '@/features/notification/services/useNotificationQueries';
import { workspaceService } from '@/features/workspace/services/workspaceService';

export type UpdateTodoParams = { id: string } & Partial<Omit<Todo, 'id'>>;

export function useInboxTodos(userId: string | undefined, enabled = true) {
    return useQuery<TodoWithDetails[], Error>({
        queryKey: ['todos', 'inbox'],
        queryFn: () =>
            todoService.getInboxTodos({
                userId: userId,
            }),
        enabled: !!userId && enabled, // userId가 있을 때만 쿼리 실행
    });
}

// 멤버 미완료 할 일 목록 조회 훅
export function useGetNotDoneTodos(
    userId: string,
    goalIds: string[],
    options?: Partial<UseQueryOptions<TodoWithGoal[], Error>>
) {
    return useQuery<TodoWithGoal[], Error>({
        queryKey: ['todos', 'not-done', userId, goalIds],
        queryFn: () => todoService.getNotDoneTodos(userId, goalIds),
        enabled: !!userId && options?.enabled !== false,
        ...options,
    });
}

// 멤버 완료 할 일 목록 조회 훅 - 연결된 목표가 없거나, active, done 상태인 목표의 할 일 조회
export function useGetDoneTodos(
    userId: string,
    goalIds: string[],
    options?: Partial<UseQueryOptions<TodoWithGoal[], Error>>
) {
    return useQuery<TodoWithGoal[], Error>({
        queryKey: ['todos', 'done', userId, goalIds],
        queryFn: () => todoService.getDoneTodos(userId, goalIds),
        enabled: !!userId && options?.enabled !== false,
        ...options,
    });
}

// 단일 할 일 조회 훅
export function useTodo(todoId: string) {
    return useSafeQuery<TodoWithDetails, Error>({
        queryKey: ['todo', todoId],
        queryFn: () => todoService.getTodoWithDetailsById(todoId),
        enabled: !!todoId, // todoId가 있을 때만 쿼리 실행
    });
}

export function useGetDeletedTodoTitle(todoId: string) {
    return useQuery<string, Error>({
        queryKey: ['deleted-todo', todoId],
        queryFn: async () => {
            const todo = await todoService.getTodoById(todoId);
            return todo?.title || '';
        },
        enabled: !!todoId,
    });
}

// 특정 목표의 할 일 목록 조회 훅
export function useTodosByGoal(goalId: string) {
    return useSafeQuery<TodoWithDetails[], Error>({
        queryKey: ['todos', 'goal', goalId],
        queryFn: () => todoService.findByGoalId(goalId),
        enabled: !!goalId,
    });
}

// 할 일 생성 뮤테이션 훅
export function useCreateTodo(onSettled?: () => void) {
    const queryClient = useQueryClient();

    // 알림 발송 함수
    const { createTodoAssignedNotification } = useTodoAssignedNotification();

    return useMutation<TodoWithUser, Error, CreateTodoInput>({
        mutationFn: async newTodo => await todoService.createTodo(newTodo),
        onSuccess: async createdTodo => {
            // 모든 Todo 캐시 무효화 (enable 필드가 true인 경우에만)
            if (createdTodo.enable) {
                await queryClient.invalidateQueries({ queryKey: ['todos'] });
            }

            // 플래너 관련 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['planners', 'selectedDate'],
            });
            await queryClient.invalidateQueries({
                queryKey: ['planners', 'unfinished'],
            });

            // 목표 목록 캐시 무효화
            if (createdTodo.goal_id) {
                await queryClient.invalidateQueries({
                    queryKey: ['goal', 'team'],
                });

                await queryClient.invalidateQueries({
                    queryKey: ['goal', createdTodo.goal_id],
                });
            }

            // 할일 담당자에게 알림 발송, 방금 생성했으므로 active 상태 체크 필요 X
            const { user } = useAuthStore.getState();
            const targetUserId = createdTodo.user_id;

            if (user && user.id != targetUserId) {
                try {
                    await createTodoAssignedNotification({
                        userId: targetUserId,
                        todoId: createdTodo.id,
                        todoAssignedContent: {
                            todo_title: createdTodo.title,
                            assigner_name: user.name,
                            assigner_image_url: user.profile_image_url,
                            assigner_id: user.id,
                            goal_id: createdTodo.goal_id,
                        },
                    });
                } catch (error) {
                    console.error('알림 발송 실패: ', error);
                    // 주요 업데이트는 이미 성공했으므로 에러를 무시
                }
            }
        },
        onSettled: onSettled,
    });
}

// 할 일 업데이트 뮤테이션 훅
export function useUpdateTodo() {
    const queryClient = useQueryClient();
    const { user } = useAuthStore.getState();

    // 알림 발송 함수
    const { createTodoDeadlineChangedNotification } =
        useTodoDeadlineChangedNotification();
    const { createTodoStateChangedNotification } =
        useTodoStateChangedNotification();
    const deleteNotificationSchedule = useDeleteSchedule();

    return useMutation<Todo, Error, UpdateTodoParams, UpdateContext<Todo>>({
        mutationFn: params => {
            if (!params.id) return;

            const { id, ...todoFields } = params;
            return todoService.updateTodo(id, todoFields);
        },

        onMutate: async variables => {
            // 이전 데이터 가져오기
            const oldTodoData = await todoService.getTodoById(variables.id);

            // 변경 유형 확인
            const changeTypes = {
                isStatusChange:
                    isDifferent(
                        variables.complete_date,
                        oldTodoData.complete_date
                    ) ||
                    isDifferent(
                        variables.cancelled_date,
                        oldTodoData.cancelled_date
                    ),
                isDeadlineChange: isDifferent(
                    variables.due_date,
                    oldTodoData.due_date
                ),
            };

            // 변경된 데이터가 없으면 리턴
            if (!changeTypes.isDeadlineChange && !changeTypes.isStatusChange) {
                return;
            }

            // 변경된 데이터 반환
            return {
                oldData: oldTodoData,
                changeTypes,
            };
        },

        onSuccess: async (updatedTodo, variables, context) => {
            // 모든 할 일 관련 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['todos'] });

            // 플래너 관련 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['planners', 'selectedDate'],
            });
            await queryClient.invalidateQueries({
                queryKey: ['planners', 'unfinished'],
            });

            // 특정 todo와 관련된 쿼리도 무효화
            await queryClient.invalidateQueries({
                queryKey: ['todo', updatedTodo.id],
            });

            // goal_id가 있으면 해당 goal 관련 쿼리도 무효화
            if (updatedTodo.goal_id) {
                await queryClient.invalidateQueries({
                    queryKey: ['goal', 'team'],
                });

                await queryClient.invalidateQueries({
                    queryKey: ['goal', updatedTodo.goal_id],
                });

                await queryClient.invalidateQueries({
                    queryKey: ['goal', 'user', user?.id],
                });
            }

            // 변경 관련 알림 발송
            if (!context || !context.oldData) {
                // PlannerTextfield에서 enable false 상태로 추가 후 enable true로 업데이트할 때 여기를 탐
                // 버그 아니어서 콘솔 로그 X
                // console.log(
                //     'Failed to send todo update notification: context or oldData is undefined'
                // );
                return;
            }

            try {
                const { user } = useAuthStore.getState();
                const targetUserId = updatedTodo.user_id;

                // 완료 상태로 변경 시, 알림 스케줄링 데이터 삭제
                if (
                    context.changeTypes.isStatusChange &&
                    (updatedTodo.cancelled_date !== null ||
                        updatedTodo.complete_date !== null)
                ) {
                    await deleteNotificationSchedule.mutateAsync({
                        relatedType: 'todo',
                        relatedId: updatedTodo.id,
                        notificationType:
                            NotificationType.TODO_DEADLINE_REMINDER,
                    });
                }

                // 알림 발송 조건 체크
                if (!user || user.id === targetUserId) return;
                if (
                    // Active 상태인 투두거나 상태 변경일 경우에만 알림 발송
                    !checkSendNotificationByTodoStatus(
                        updatedTodo,
                        context.changeTypes
                    )
                ) {
                    return;
                }

                const workspaceData =
                    await workspaceService.getWorkspaceByGoalId(
                        updatedTodo.goal_id
                    );
                const targetUserWorkspaceMember =
                    workspaceData?.workspace_member.find(
                        (member: WorkspaceMemberWithUser) =>
                            member.user.id === targetUserId
                    );
                if (!targetUserWorkspaceMember) return;

                const { changeTypes } = context;
                const notificationPromises = [];
                const todoId = updatedTodo.id;

                // 공통 데이터
                const commonData = {
                    // todoId: updatedTodo.id,
                    todo_title: updatedTodo.title,
                    changer_name: user.name,
                    changer_image_url: user.profile_image_url,
                    changer_id: user.id,
                    // goalId: updatedTodo.goal_id,
                };

                // 1. 상태 변경 알림
                if (
                    changeTypes.isStatusChange &&
                    (variables.complete_date !== undefined ||
                        variables.cancelled_date !== undefined)
                ) {
                    const oldState = getTodoStatus(context.oldData);
                    const newState = getTodoStatus(updatedTodo);

                    notificationPromises.push(
                        createTodoStateChangedNotification({
                            userId: targetUserId,
                            todoId,
                            todoStateChangedContent: {
                                ...commonData,
                                old_state: oldState,
                                new_state: newState,
                            },
                        })
                    );
                }

                // 2. 마감일 변경 알림
                if (changeTypes.isDeadlineChange && variables.due_date) {
                    notificationPromises.push(
                        createTodoDeadlineChangedNotification({
                            userId: targetUserId,
                            todoId,
                            todoDeadlineChangedContent: {
                                ...commonData,
                                old_deadline: context.oldData.due_date,
                                new_deadline: variables.due_date,
                            },
                        })
                    );
                }

                // 알림 전송
                await Promise.all(notificationPromises);
            } catch (error) {
                console.error('알림 발송 실패:', error);
                // 주요 업데이트는 이미 성공했으므로 에러를 무시
            }
        },
    });
}

// 할 일 삭제 뮤테이션 훅
export function useDeleteTodo() {
    const queryClient = useQueryClient();
    const deleteNotificationSchedule = useDeleteSchedule();

    return useMutation<void, Error, { todoId: string | undefined }>({
        mutationFn: ({ todoId }) => todoService.deleteTodo(todoId),
        onSuccess: async (_, variables) => {
            // 관련된 모든 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['todos'] });
            await queryClient.invalidateQueries({ queryKey: ['planners'] });

            // 특정 Todo 캐시 제거
            if (variables.todoId) {
                queryClient.removeQueries({
                    queryKey: ['todo', variables.todoId],
                });
                await queryClient.invalidateQueries({
                    queryKey: ['planners', 'todo', variables.todoId],
                });
            }

            // 스케줄 삭제
            try {
                await deleteNotificationSchedule.mutateAsync({
                    relatedType: 'todo',
                    relatedId: variables.todoId,
                    notificationType: NotificationType.TODO_DEADLINE_REMINDER,
                });
            } catch (error) {
                console.error('Failed to delete notification schedule:', error);
            }
        },
        onError: error => {
            console.error('Failed to delete todo:', error);
        },
    });
}

// Todo 댓글 관련 쿼리
export function useTodoComments(todoId: string | undefined) {
    return useComments('todo', todoId);
}
